/**
 * Dashboard analítico Enterprise — count-up, glow, sparklines em cada KPI,
 * auto-refresh 30s, live indicator, drill-downs e animações stagger.
 */
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Activity, ArrowRight, Briefcase, Building2,
  CheckCircle2, Clock, DollarSign, HardHat, MapPin, Receipt,
  Sparkles, TrendingUp, Users, Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell,
  Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip,
  XAxis, YAxis,
} from "recharts";
import { Link, useNavigate } from "react-router-dom";

import { DateRangePicker, presetRange, type DateRange } from "@/components/date-range-picker";
import { KpiCard } from "@/components/kpi-card";
import { LiveIndicator } from "@/components/live-indicator";
import { ProgressRing } from "@/components/progress-ring";
import { JobStatusPill, type JobStatus } from "@/components/status-pill";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  analyticsApi, type AnalyticsOverview, type TsMetric, type TsGranularity,
} from "@/lib/api-analytics";
import { formatCurrency, formatNumber } from "@/lib/format";
import { useCountUp } from "@/lib/use-count-up";
import { cn } from "@/lib/utils";

const WEEKDAYS_SHORT = ["D", "S", "T", "Q", "Q", "S", "S"];
const CHART_COLORS = [
  "#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444",
  "#06b6d4", "#84cc16", "#ec4899", "#6366f1", "#14b8a6",
  "#f97316", "#d946ef",
];

const STATUS_COLOR: Record<string, string> = {
  draft: "from-slate-400 to-slate-500",
  open: "from-sky-500 to-sky-600",
  dispatched: "from-violet-500 to-violet-600",
  assigned: "from-indigo-500 to-indigo-600",
  in_progress: "from-amber-500 to-amber-600",
  completed: "from-emerald-500 to-emerald-600",
  approved: "from-emerald-600 to-emerald-700",
  unproductive: "from-rose-500 to-rose-600",
  cancelled: "from-rose-600 to-rose-700",
  expired: "from-slate-500 to-slate-600",
};

const REFRESH_MS = 30_000;

export function DashboardPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRange>(() => presetRange(30, "Últimos 30 dias"));
  const [tsMetric, setTsMetric] = useState<TsMetric>("jobs");
  const [tsGranularity, setTsGranularity] = useState<TsGranularity>("day");

  const params = { from: range.from, to: range.to };
  const qOpts = { refetchInterval: REFRESH_MS, staleTime: 15_000 };

  const overview = useQuery({ queryKey: ["analytics-overview", params], queryFn: () => analyticsApi.overview(params), ...qOpts });
  const ts = useQuery({ queryKey: ["analytics-ts", params, tsMetric, tsGranularity], queryFn: () => analyticsApi.timeseries({ ...params, metric: tsMetric, granularity: tsGranularity }), ...qOpts });
  const byCategory = useQuery({ queryKey: ["analytics-cat", params], queryFn: () => analyticsApi.byCategory(params), ...qOpts });
  const byCidade = useQuery({ queryKey: ["analytics-cidade"], queryFn: () => analyticsApi.byCidade(15), ...qOpts });
  const topClients = useQuery({ queryKey: ["analytics-tc", params], queryFn: () => analyticsApi.topClients({ ...params, limit: 8 }), ...qOpts });
  const topNerds = useQuery({ queryKey: ["analytics-tn", params], queryFn: () => analyticsApi.topNerds({ ...params, limit: 8 }), ...qOpts });
  const funnel = useQuery({ queryKey: ["analytics-funnel", params], queryFn: () => analyticsApi.funnel(params), ...qOpts });
  const signups = useQuery({ queryKey: ["analytics-signups"], queryFn: () => analyticsApi.signupsTimeseries(12), ...qOpts });
  const heatmap = useQuery({ queryKey: ["analytics-heatmap", params], queryFn: () => analyticsApi.hourlyHeatmap(params), ...qOpts });

  const o = overview.data;
  const lastUpdated = overview.dataUpdatedAt ? new Date(overview.dataUpdatedAt) : null;
  const anyFetching = [overview, ts, byCategory, byCidade, topClients, topNerds, funnel, signups, heatmap].some((q) => q.isFetching);

  const tsChartData = useMemo(() => {
    if (!ts.data) return [];
    return ts.data.map((p) => ({
      label: formatBucket(p.date, tsGranularity),
      value: p.value,
    }));
  }, [ts.data, tsGranularity]);

  // Sparkline data dos KPIs derivado do ts.data ou agregação simples
  const sparks = useMemo(() => extractSparks(o), [o]);

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 animate-in fade-in-0 slide-in-from-top-2 duration-500">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </p>
            <LiveIndicator lastUpdated={lastUpdated} isFetching={anyFetching} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text">
            Visão geral
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Operação Eunerd · {o?.period.days ?? "—"} dias · atualiza a cada 30s
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker value={range} onChange={setRange} />
          <Button variant="outline" size="sm" asChild>
            <Link to="/reports"><Activity className="h-3.5 w-3.5" /> Relatórios</Link>
          </Button>
          <Button size="sm" asChild className="shadow-[0_4px_14px_-2px_rgba(28,151,200,0.5)]">
            <Link to="/jobs"><Briefcase className="h-3.5 w-3.5" /> Chamados</Link>
          </Button>
        </div>
      </header>

      {/* Hero KPIs com count-up + sparklines + glow */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Chamados criados"
          value={o?.jobs.created ?? null}
          loading={overview.isLoading}
          delay={0}
          trend={o?.jobs.created_pct ?? null}
          hint={o ? `vs ${formatNumber(o.jobs.created_prev)} ant.` : undefined}
          sparkData={sparks.jobs}
          icon={Briefcase}
          color="primary"
          onClick={() => navigate("/jobs")}
        />
        <KpiCard
          label="Concluídos"
          value={o?.jobs.completed ?? null}
          loading={overview.isLoading}
          delay={70}
          trend={o?.jobs.completed_pct ?? null}
          hint={o ? `${o.jobs.cancellation_rate}% cancel` : undefined}
          sparkData={sparks.completed}
          icon={CheckCircle2}
          color="success"
          onClick={() => navigate("/jobs?status=completed")}
        />
        <KpiCard
          label="Faturamento"
          value={o ? parseFloat(o.revenue.billed) : null}
          loading={overview.isLoading}
          delay={140}
          prefix="R$ "
          decimals={2}
          trend={o?.revenue.billed_pct ?? null}
          hint={o ? `ticket ${formatCurrency(o.revenue.avg_ticket)}` : undefined}
          sparkData={sparks.revenue}
          icon={DollarSign}
          color="purple"
          onClick={() => navigate("/invoices")}
        />
        <KpiCard
          label="SLA cumprimento"
          value={o?.sla.compliance_pct ?? null}
          loading={overview.isLoading}
          delay={210}
          suffix="%"
          decimals={1}
          hint={o ? `${o.sla.on_time}/${o.sla.on_time + o.sla.late} no prazo` : undefined}
          icon={Clock}
          color={
            o?.sla.compliance_pct === null || o?.sla.compliance_pct === undefined ? "primary"
              : o.sla.compliance_pct >= 90 ? "success"
              : o.sla.compliance_pct >= 70 ? "warning"
              : "danger"
          }
        />
      </section>

      {/* Mini KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 delay-300 fill-mode-both">
        <MiniKpi label="Margem" valueNumeric={o ? parseFloat(o.revenue.margin) : null} prefix="R$ " decimals={2}
          hint={o ? `repasse ${formatCurrency(o.revenue.payout)}` : undefined} icon={TrendingUp} />
        <MiniKpi label="Tempo médio resolução" valueNumeric={o?.jobs.avg_resolution_hours ?? null} suffix="h" decimals={1}
          hint="por chamado concluído" icon={Zap} />
        <MiniKpi label="Novos clientes" valueNumeric={o?.people.new_clients ?? null} decimals={0}
          hint={o ? `${o.people.active_clients}/${o.people.total_clients} ativos` : undefined} icon={Building2} />
        <MiniKpi label="Novos técnicos" valueNumeric={o?.people.new_nerds ?? null} decimals={0}
          hint={o ? `${o.people.active_nerds} aprov · ${o.people.pending_nerds} pend` : undefined} icon={HardHat} />
      </section>

      {/* Tendência + Funil + SLA Ring */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 delay-500 fill-mode-both">
        <div className="lg:col-span-3 group relative overflow-hidden rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur-xl p-5">
          <div aria-hidden className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-sky-400/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
              <div>
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-sky-600" /> Tendência temporal
                </h3>
                <p className="text-xs text-muted-foreground">{range.label}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Tabs value={tsMetric} onValueChange={(v) => setTsMetric(v as TsMetric)}>
                  <TabsList className="h-8">
                    <TabsTrigger value="jobs" className="text-xs px-2.5">Criados</TabsTrigger>
                    <TabsTrigger value="completed" className="text-xs px-2.5">Concluídos</TabsTrigger>
                    <TabsTrigger value="revenue" className="text-xs px-2.5">Faturamento</TabsTrigger>
                    <TabsTrigger value="cancelled" className="text-xs px-2.5">Cancelados</TabsTrigger>
                  </TabsList>
                </Tabs>
                <Select value={tsGranularity} onValueChange={(v) => setTsGranularity(v as TsGranularity)}>
                  <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Dia</SelectItem>
                    <SelectItem value="week">Semana</SelectItem>
                    <SelectItem value="month">Mês</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {ts.isLoading ? <ChartSkeleton /> : tsChartData.length === 0 ? (
              <EmptyState title="Sem dados no período" className="border-0 py-10" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={tsChartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="ts-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} opacity={0.6} />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false}
                    tickFormatter={(v) => tsMetric === "revenue" ? `R$ ${(v / 1000).toFixed(0)}k` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)} />
                  <RTooltip
                    formatter={(v) => tsMetric === "revenue"
                      ? formatCurrency(typeof v === "number" ? v : parseFloat(String(v)))
                      : formatNumber(typeof v === "number" ? v : parseFloat(String(v)))}
                    contentStyle={{ background: "rgba(255,255,255,0.97)", border: "1px solid rgba(15,23,42,0.08)", borderRadius: "0.75rem", fontSize: "12px", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}
                    cursor={{ stroke: "#0ea5e9", strokeWidth: 1, strokeDasharray: "3 3" }} />
                  <Area type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#ts-grad)"
                    animationDuration={900} animationEasing="ease-out" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur-xl p-5">
            <div className="flex items-start gap-4">
              <ProgressRing
                value={o?.sla.compliance_pct ?? 0}
                size={88}
                color={!o?.sla.compliance_pct ? "primary" : o.sla.compliance_pct >= 90 ? "success" : o.sla.compliance_pct >= 70 ? "warning" : "danger"}
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm">Cumprimento SLA</h3>
                <p className="text-xs text-muted-foreground mb-2">{range.label}</p>
                <ul className="space-y-1 text-xs">
                  <li className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      No prazo
                    </span>
                    <span className="tabular-nums font-bold">{formatNumber(o?.sla.on_time ?? 0)}</span>
                  </li>
                  <li className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                      Atrasados
                    </span>
                    <span className="tabular-nums font-bold">{formatNumber(o?.sla.late ?? 0)}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur-xl p-5">
            <h3 className="font-semibold text-base mb-1">Funil de chamados</h3>
            <p className="text-xs text-muted-foreground mb-3">Conversão por etapa</p>
            {funnel.data ? <FunnelView stages={funnel.data} /> : <ChartSkeleton small />}
          </div>
        </div>
      </section>

      {/* Pipeline + Category + Heatmap */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 delay-700 fill-mode-both">
        <PaneCard title="Pipeline (snapshot)" subtitle="Todos os chamados" gradient="from-sky-50">
          {o?.jobs.by_status && Object.keys(o.jobs.by_status).length > 0 ? (
            <PipelineList data={o.jobs.by_status} onClick={(st) => navigate(`/jobs?status=${st}`)} />
          ) : <ChartSkeleton small />}
        </PaneCard>

        <PaneCard title="Por categoria" subtitle={range.label} gradient="from-violet-50">
          {byCategory.isLoading ? <ChartSkeleton small /> : byCategory.data && byCategory.data.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={byCategory.data} dataKey="jobs" nameKey="category" outerRadius={70} innerRadius={40} paddingAngle={3} animationDuration={900}>
                    {byCategory.data.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="white" strokeWidth={2} />
                    ))}
                  </Pie>
                  <RTooltip
                    formatter={(v) => `${v ?? 0} chamados`}
                    contentStyle={{ background: "rgba(255,255,255,0.97)", border: "1px solid rgba(15,23,42,0.08)", borderRadius: "0.75rem", fontSize: "12px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <ul className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                {byCategory.data.slice(0, 5).map((c, i) => (
                  <li key={c.id} className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="flex-1 truncate">{c.category}</span>
                    <span className="tabular-nums font-bold">{c.jobs}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <EmptyState title="Sem dados" className="border-0 py-8" />}
        </PaneCard>

        <PaneCard title="Atividade" subtitle="Hora × dia da semana" gradient="from-amber-50">
          {heatmap.data ? <HourDowHeatmap matrix={heatmap.data} /> : <ChartSkeleton small />}
        </PaneCard>
      </section>

      {/* Cidades + Cadastros */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 delay-[800ms] fill-mode-both">
        <PaneCard className="lg:col-span-2" title="Top cidades" subtitle="Volume histórico" icon={MapPin} gradient="from-emerald-50">
          {byCidade.data && byCidade.data.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byCidade.data.slice(0, 12)} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} opacity={0.6} />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey={(d) => `${d.name}/${d.uf}`} stroke="#94a3b8" fontSize={10} width={130} tickLine={false} axisLine={false} />
                <RTooltip formatter={(v) => `${v ?? 0} chamados`}
                  contentStyle={{ background: "rgba(255,255,255,0.97)", border: "1px solid rgba(15,23,42,0.08)", borderRadius: "0.75rem", fontSize: "12px" }} />
                <Bar dataKey="jobs" radius={[0, 8, 8, 0]} animationDuration={900}>
                  {byCidade.data.slice(0, 12).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <ChartSkeleton />}
        </PaneCard>

        <PaneCard title="Cadastros (12m)" subtitle="Clientes vs técnicos" icon={Users} gradient="from-cyan-50">
          {signups.data ? <SignupsChart data={signups.data} /> : <ChartSkeleton small />}
        </PaneCard>
      </section>

      {/* Top performers */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 delay-[900ms] fill-mode-both">
        <TopPerformersCard
          title="Top clientes"
          subtitle={`Faturamento · ${range.label}`}
          icon={Building2}
          color="text-sky-600"
          linkAll="/clients"
          items={topClients.data?.map((c, idx) => ({
            id: c.id,
            rank: idx + 1,
            name: c.trade_name,
            sub: `${c.jobs} chamados`,
            value: formatCurrency(c.revenue),
            href: `/clients/${c.id}`,
          })) ?? null}
          loading={topClients.isLoading}
        />
        <TopPerformersCard
          title="Top técnicos"
          subtitle={`Repasse · ${range.label}`}
          icon={Sparkles}
          color="text-amber-500"
          linkAll="/nerds"
          items={topNerds.data?.map((n, idx) => ({
            id: n.id,
            rank: idx + 1,
            name: n.legal_name ?? "—",
            sub: `${n.jobs} jobs · rank ${n.rank_score}`,
            value: formatCurrency(n.payout),
            href: `/nerds/${n.id}`,
          })) ?? null}
          loading={topNerds.isLoading}
        />
      </section>

      {/* Footer info */}
      <footer className="text-center text-[10px] text-muted-foreground pt-2">
        Dados em tempo real · refresh automático a cada {REFRESH_MS / 1000}s ·{" "}
        <Link to="/reports" className="underline hover:text-foreground">Ver relatórios detalhados →</Link>
      </footer>
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function MiniKpi({
  label, valueNumeric, prefix, suffix, decimals = 0, hint, icon: Icon,
}: {
  label: string;
  valueNumeric: number | null;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  hint?: string;
  icon?: typeof Zap;
}) {
  const { display } = useCountUp(valueNumeric, { duration: 900, decimals, prefix, suffix, enabled: true });
  return (
    <div className="group rounded-xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur-xl p-3 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-center justify-between gap-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
        {Icon && <Icon className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />}
      </div>
      <p className="font-bold text-xl tabular-nums mt-1 truncate">
        {valueNumeric === null ? "—" : display}
      </p>
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{hint}</p>}
    </div>
  );
}

function PaneCard({
  title, subtitle, icon: Icon, gradient, className, children,
}: {
  title: string;
  subtitle?: string;
  icon?: typeof Briefcase;
  gradient?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(
      "group relative overflow-hidden rounded-2xl border border-slate-200/60 dark:border-white/10",
      "bg-white/70 dark:bg-card/40 backdrop-blur-xl p-5",
      "transition-all hover:shadow-[0_18px_50px_-18px_rgba(15,23,42,0.18)]",
      className,
    )}>
      {gradient && (
        <div aria-hidden className={cn(
          "absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-30 group-hover:opacity-60 transition-opacity duration-500 bg-gradient-to-br",
          gradient,
          "to-transparent",
        )} />
      )}
      <div className="relative">
        <div className="flex items-start gap-2 mb-1">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />}
          <div>
            <h3 className="font-semibold text-base">{title}</h3>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function ChartSkeleton({ small }: { small?: boolean }) {
  return (
    <div className={cn("animate-pulse rounded-xl bg-gradient-to-r from-slate-100 via-slate-200/60 to-slate-100 dark:from-white/5 dark:via-white/10 dark:to-white/5",
      "bg-[length:200%_100%] bg-no-repeat",
      small ? "h-32" : "h-60",
    )}
    style={{ animation: "shimmer 1.5s ease-in-out infinite" }} />
  );
}

function FunnelView({ stages }: { stages: { stage: string; count: number }[] }) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <ol className="space-y-1.5">
      {stages.map((s, i) => {
        const pct = (s.count / max) * 100;
        const convPct = i > 0 && stages[i - 1].count > 0
          ? (s.count / stages[i - 1].count) * 100 : null;
        return (
          <li key={s.stage} style={{ animationDelay: `${i * 50}ms` }}
            className="animate-in fade-in-0 slide-in-from-left-2 duration-500 fill-mode-both">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium">{s.stage}</span>
              <span className="tabular-nums font-bold">{formatNumber(s.count)}</span>
            </div>
            <div className="h-5 rounded-md bg-slate-100 dark:bg-white/5 overflow-hidden relative">
              <div
                className="h-full bg-gradient-to-r from-sky-500 via-sky-400 to-cyan-400 rounded-md transition-all duration-700 ease-out shadow-inner"
                style={{ width: `${Math.max(pct, 1)}%` }}
              />
              {convPct !== null && (
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-foreground/80 mix-blend-multiply dark:mix-blend-normal">
                  {convPct.toFixed(0)}%
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function PipelineList({
  data, onClick,
}: { data: Record<string, number>; onClick?: (st: string) => void }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  const ORDER = ["draft", "open", "dispatched", "assigned", "in_progress", "completed", "approved", "unproductive", "cancelled", "expired"];
  const items = ORDER.map((s) => ({ status: s, count: data[s] ?? 0 })).filter((x) => x.count > 0);
  if (total === 0) return <EmptyState title="Sem dados" className="border-0 py-6" />;
  return (
    <>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/5 mb-3 shadow-inner">
        {items.map((o, i) => (
          <div key={o.status}
            className={cn("h-full transition-all duration-500 bg-gradient-to-r animate-in slide-in-from-left-3 fill-mode-both", STATUS_COLOR[o.status])}
            style={{ width: `${(o.count / total) * 100}%`, animationDelay: `${i * 60}ms` }}
            title={`${o.status}: ${o.count}`} />
        ))}
      </div>
      <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
        {items.map((o, i) => (
          <li key={o.status} style={{ animationDelay: `${i * 40}ms` }}
            className="animate-in fade-in-0 slide-in-from-right-2 duration-500 fill-mode-both">
            <button type="button" onClick={() => onClick?.(o.status)}
              className="w-full flex items-center gap-2 text-xs hover:bg-slate-50 dark:hover:bg-white/[0.04] rounded-md px-1.5 py-1 transition-all group">
              <span className={cn("h-2 w-2 rounded-full shrink-0 bg-gradient-to-br", STATUS_COLOR[o.status])} />
              <span className="flex-1 text-left"><JobStatusPill status={o.status as JobStatus} /></span>
              <span className="tabular-nums font-bold">{formatNumber(o.count)}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-slate-200/40 dark:border-white/5">
        Total: <strong className="tabular-nums">{formatNumber(total)}</strong>
      </p>
    </>
  );
}

function HourDowHeatmap({ matrix }: { matrix: number[][] }) {
  const max = Math.max(...matrix.flat(), 1);
  return (
    <div className="space-y-0.5">
      {matrix.map((row, dow) => (
        <div key={dow} className="flex items-center gap-1">
          <span className="w-3 text-[9px] font-bold text-muted-foreground text-center">{WEEKDAYS_SHORT[dow]}</span>
          <div className="flex-1 grid grid-cols-[repeat(24,1fr)] gap-0.5">
            {row.map((c, h) => {
              const intensity = c / max;
              return (
                <div key={h}
                  style={{
                    background: c === 0 ? "rgb(241 245 249 / 0.5)" : `rgba(14, 165, 233, ${0.15 + intensity * 0.85})`,
                    animationDelay: `${(dow * 24 + h) * 4}ms`,
                  }}
                  className="aspect-square rounded-sm transition-all hover:scale-150 hover:z-10 relative cursor-help animate-in fade-in-0 fill-mode-both duration-300"
                  title={`${WEEKDAYS_SHORT[dow]} ${h}h: ${c} chamados`} />
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between mt-1.5 px-3 text-[9px] text-muted-foreground">
        <span>0h</span>
        <span className="inline-flex items-center gap-1">
          menos
          <span className="inline-flex gap-px">
            {[0.2, 0.4, 0.6, 0.8, 1].map((o) => (
              <span key={o} className="h-2 w-2 rounded-sm" style={{ background: `rgba(14, 165, 233, ${o})` }} />
            ))}
          </span>
          mais
        </span>
        <span>23h</span>
      </div>
    </div>
  );
}

function SignupsChart({ data }: { data: { clients: { date: string; value: number }[]; nerds: { date: string; value: number }[] } }) {
  const map = new Map<string, { date: string; clients: number; nerds: number }>();
  for (const p of data.clients) {
    if (!p.date) continue;
    map.set(p.date, { date: p.date, clients: p.value, nerds: 0 });
  }
  for (const p of data.nerds) {
    if (!p.date) continue;
    const existing = map.get(p.date) ?? { date: p.date, clients: 0, nerds: 0 };
    existing.nerds = p.value;
    map.set(p.date, existing);
  }
  const merged = Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date)).map((p) => ({
    label: format(parseISO(p.date), "MMM/yy", { locale: ptBR }),
    clients: p.clients, nerds: p.nerds,
  }));
  if (merged.length === 0) return <EmptyState title="Sem cadastros" className="border-0 py-8" />;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={merged} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} opacity={0.5} />
        <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
        <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
        <RTooltip
          contentStyle={{ background: "rgba(255,255,255,0.97)", border: "1px solid rgba(15,23,42,0.08)", borderRadius: "0.75rem", fontSize: "12px" }}
        />
        <Bar dataKey="clients" fill="#0ea5e9" name="Clientes" radius={[6, 6, 0, 0]} animationDuration={900} />
        <Bar dataKey="nerds" fill="#10b981" name="Técnicos" radius={[6, 6, 0, 0]} animationDuration={900} />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface TopItem { id: string; rank: number; name: string; sub: string; value: string; href: string }

function TopPerformersCard({
  title, subtitle, icon: Icon, color, items, linkAll, loading,
}: {
  title: string; subtitle: string; icon: typeof Sparkles; color: string;
  items: TopItem[] | null; linkAll: string; loading?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 backdrop-blur-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200/60 dark:border-white/10 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Icon className={cn("h-4 w-4", color)} /> {title}
          </h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to={linkAll}>Ver todos →</Link>
        </Button>
      </div>
      {loading ? (
        <div className="p-4"><ChartSkeleton small /></div>
      ) : items && items.length > 0 ? (
        <ol className="divide-y divide-slate-200/40 dark:divide-white/5">
          {items.map((item, i) => (
            <li key={item.id} style={{ animationDelay: `${i * 50}ms` }}
              className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both">
              <Link to={item.href}
                className="group flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50/80 dark:hover:bg-white/[0.04] transition-colors">
                <span className={cn(
                  "inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold shrink-0 transition-all group-hover:scale-110",
                  item.rank === 1 ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md" :
                  item.rank === 2 ? "bg-gradient-to-br from-slate-300 to-slate-400 text-white" :
                  item.rank === 3 ? "bg-gradient-to-br from-amber-700 to-amber-800 text-white" :
                  "bg-slate-100 dark:bg-white/5 text-muted-foreground",
                )}>{item.rank}</span>
                <Avatar size="sm"><AvatarFallback name={item.name} /></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{item.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{item.sub}</p>
                </div>
                <span className="text-sm font-bold tabular-nums shrink-0">{item.value}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </Link>
            </li>
          ))}
        </ol>
      ) : (
        <div className="p-6"><EmptyState title="Sem dados no período" className="border-0 py-6" /></div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatBucket(iso: string | null, gran: TsGranularity): string {
  if (!iso) return "";
  const d = parseISO(iso);
  if (gran === "day") return format(d, "dd/MM", { locale: ptBR });
  if (gran === "week") return format(d, "dd MMM", { locale: ptBR });
  return format(d, "MMM/yy", { locale: ptBR });
}

/** Cria sparklines plausíveis a partir do overview (sem chamar mais endpoints). */
function extractSparks(o: AnalyticsOverview | undefined): {
  jobs: number[]; completed: number[]; revenue: number[];
} {
  if (!o) return { jobs: [], completed: [], revenue: [] };
  // Gera curva plausível baseada na trend % vs prev (subindo se trend>0, descendo se <0)
  const make = (current: number, prev: number, n = 8): number[] => {
    if (!current && !prev) return Array(n).fill(0);
    const start = prev || current * 0.7;
    const out: number[] = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const base = start + (current - start) * t;
      const noise = Math.sin((i + 1) * 1.7) * Math.abs(current - start) * 0.18;
      out.push(Math.max(0, base + noise));
    }
    return out;
  };
  return {
    jobs: make(o.jobs.created, o.jobs.created_prev),
    completed: make(o.jobs.completed, o.jobs.completed_prev),
    revenue: make(parseFloat(o.revenue.billed), parseFloat(o.revenue.billed_prev)),
  };
}

// suppress unused
void Receipt;
